/* eslint-disable react-refresh/only-export-components */
// theme.tsx
// Rendimiento: evita re-render del árbol con store global + useSyncExternalStore.
// Sin dependencias nuevas. SSR-safe. Mantiene el mismo contrato externo.

import React, {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useSyncExternalStore,
  useEffect,
  useLayoutEffect,
} from 'react';

// Tipos para el contexto y el hook (mismos exports)
export type Theme = 'light' | 'dark';

interface ThemeContextProps {
  theme: Theme;              // se mantiene en el hook (via store)
  toggleTheme: () => void;   // acción estable
  setTheme: (theme: Theme) => void; // acción estable
}

// -----------------------------------------------------------------------------
// Store global (no re-render del Provider ni del árbol; sólo re-render de consumidores)
// -----------------------------------------------------------------------------
type ThemeState = {
  theme: Theme;
  // true si el usuario eligió explícitamente (localStorage), false = seguir sistema
  hasUserPref: boolean;
};

let state: ThemeState = { theme: 'light', hasUserPref: false };
const subs = new Set<() => void>();
let initialized = false;

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';
const useIsoLayoutEffect = isBrowser ? useLayoutEffect : useEffect;

const notify = () => subs.forEach((fn) => fn());
const getSnapshot = (): ThemeState => state;
const getServerSnapshot = (): ThemeState => ({ theme: 'light', hasUserPref: false });

function applyDom(theme: Theme) {
  if (!isBrowser) return;
  const root = document.documentElement;
  // toggle con segundo parámetro evita lecturas previas y recalculados extra
  root.classList.toggle('dark', theme === 'dark');
  try {
    // persistimos sólo si hay preferencia de usuario
    if (state.hasUserPref) localStorage.setItem('theme', theme);
  } catch {
    /* no-op */
  }
}

function setState(next: ThemeState) {
  const changed = next.theme !== state.theme || next.hasUserPref !== state.hasUserPref;
  if (!changed) return;
  state = next;
  applyDom(state.theme);
  notify();
}

function setThemeUser(theme: Theme) {
  setState({ theme, hasUserPref: true });
}

function toggleThemeUser() {
  const next: Theme = state.theme === 'dark' ? 'light' : 'dark';
  setThemeUser(next);
}

function readInitial(): ThemeState {
  if (!isBrowser) return { theme: 'light', hasUserPref: false };
  try {
    const stored = localStorage.getItem('theme');
    if (stored === 'dark' || stored === 'light') {
      return { theme: stored, hasUserPref: true };
    }
  } catch {
    /* no-op */
  }
  // seguir preferencia del sistema
  const prefersDark =
    isBrowser &&
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;
  return { theme: prefersDark ? 'dark' : 'light', hasUserPref: false };
}

function initOnce() {
  if (initialized || !isBrowser) return;
  initialized = true;

  // Estado inicial + aplicar DOM antes del primer paint (con useIsoLayoutEffect abajo)
  setState(readInitial());

  // Sync con cambios del sistema SOLO si no hay preferencia de usuario
  const mql = window.matchMedia?.('(prefers-color-scheme: dark)');
  const onMqChange = (e: MediaQueryListEvent) => {
    if (!state.hasUserPref) {
      setState({ theme: e.matches ? 'dark' : 'light', hasUserPref: false });
    }
  };
  if (mql) {
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onMqChange);
    } else {
      mql.addListener(onMqChange);
    }
  }

  // Sync entre pestañas (storage)
  const onStorage = (ev: StorageEvent) => {
    if (ev.key !== 'theme') return;
    if (ev.newValue === 'dark' || ev.newValue === 'light') {
      setState({ theme: ev.newValue, hasUserPref: true });
    } else if (ev.newValue === null) {
      // Si otra pestaña limpia, volvemos a sistema
      const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
      setState({ theme: prefersDark ? 'dark' : 'light', hasUserPref: false });
    }
  };
  window.addEventListener('storage', onStorage);

  // Limpieza global opcional al descargar
  window.addEventListener('pagehide', () => {
    // no desmontamos listeners en runtime normal; son ligeros y únicos
    // (si se requiere, se podría gestionar un ref-count)
  });
}

function subscribe(cb: () => void) {
  subs.add(cb);
  return () => subs.delete(cb);
}

// -----------------------------------------------------------------------------
// Contexto sólo para ACCIONES (estables). El estado viene del store.
// -----------------------------------------------------------------------------
const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

export const ThemeProvider = ({ children, scope = 'global' as 'global' | 'local' }: { children: ReactNode; scope?: 'global' | 'local' }) => {
  // Inicializa y aplica DOM antes del primer paint para evitar FOUC
  useIsoLayoutEffect(() => {
    initOnce();
    // En alcance global aplicamos clase al <html>; en local no tocamos el DOM global
    if (scope === 'global') {
      applyDom(state.theme);
    }
  }, [scope]);

  const value = useMemo<ThemeContextProps>(
    () => ({
      get theme() {
        // getter para mantener compatibilidad aunque el estado real venga del hook
        return state.theme;
      },
      toggleTheme: () => toggleThemeUser(),
      setTheme: (t: Theme) => setThemeUser(t),
    }),
    []
  );

  // El Provider NO re-renderiza por cambios de tema porque `value` es estable.
  // Componente interno para aplicar clase .dark localmente sin tocar documentElement
  const LocalWrapper = ({ children }: { children: ReactNode }) => {
    const snap = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
    // Si es global, no se renderiza wrapper para evitar estructuras extra
    if (scope === 'global') return <>{children}</>;
    return <div className={snap.theme === 'dark' ? 'dark' : undefined}>{children}</div>;
  };

  return (
    <ThemeContext.Provider value={value}>
      <LocalWrapper>{children}</LocalWrapper>
    </ThemeContext.Provider>
  );
};

// -----------------------------------------------------------------------------
// Hook principal: mantiene el mismo contrato externo
// -----------------------------------------------------------------------------
export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme debe usarse dentro de ThemeProvider');

  // Re-render sólo cuando realmente cambia el theme/hasUserPref
  const snap = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  return {
    theme: snap.theme,
    toggleTheme: ctx.toggleTheme,
    setTheme: ctx.setTheme,
  };
};
